import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from "react-router";
import InfiniteScroll from 'react-infinite-scroll-component';
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';
import { Location } from 'history';
import client from 'lib/CommonApi/client';
import stringUtils from 'common/stringUtils';
import {
  callAlertWeb, callAppLoginWeb,
  callNativeWeb,
  footerShowEventWeb,
  hideProgressWeb,
  showProgressWeb
} from 'common/appInterface';
import { isIOS } from "react-device-detect";

const DeliveryInputSearchPage: React.FC = () => {
  /** Location State */
  const location = useLocation() as Location<LocationState>;
  /** Navigate */
  const navigate = useNavigate();
  /** 다국어처리 */
  const { t, i18n } = useTranslation();

  const screenId = "APPL13";
  const screenName = t("APPL13.title");

  const [cardApply] = useState<Partial<CardApplyStep6>>(location.state.apply);
  const [nextPage, setNextPage] = useState(1);
  const [keyword, setKeyword] = useState("");
  const [jusoList, setJusoList] = useState<Juso[]>();
  const [hasMore, setHasMore] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  /** 뒤로가기 처리 (backKeyEvent_App (APP > WEB)) */
  const callbackBackkeyEvent = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  /** 주소검색 */
  const callJusoFirstSearch = useCallback(async () => {
    showProgressWeb();
    await client.get("/api/app/apply/juso/search", { params: { ...location.state.query, currentPage: "1", keyword: keyword }}).then((response) => {
      let result = response.data?.response;
      console.log(result);

      if (response.data?.code === "00" && result?.results) {
        setNextPage(Number.parseInt(result.currentPage) + 1);
        setHasMore(Number.parseInt(result.results.length) === Number.parseInt(result.countPerPage));
        setJusoList(result.results);
      } else if (response.data?.code === "02") {
        // 세션 만료 처리
        // 로그인 페이지 이동
        callAppLoginWeb();
      } else {
        callAlertWeb("ERROR", "99", response.data?.message);
      }
    }).catch((e: AxiosError) => {
      callAlertWeb("ERROR", "99", e.message);
    }).finally(() => {
      hideProgressWeb();
    });
  }, [location, keyword]);

  /** 주소검색 Next */
  const callJusoNextSearch = useCallback(async () => {
    showProgressWeb();
    await client.get("/api/app/apply/juso/search", { params: { ...location.state.query, currentPage: nextPage, keyword: keyword }}).then((response) => {
      let result = response.data?.response;
      console.log(result);

      if (response.data?.code === "00" && result?.results) {
        setNextPage(Number.parseInt(result.currentPage) + 1);
        setHasMore(Number.parseInt(result.results.length) === Number.parseInt(result.countPerPage));
        setJusoList([ ...jusoList!, ...result.results ]);
      } else if (response.data?.code === "02") {
        // 세션 만료 처리
        // 로그인 페이지 이동
        callAppLoginWeb();
      } else {
        callAlertWeb("ERROR", "99", response.data?.message);
      }
    }).catch((e: AxiosError) => {
      callAlertWeb("ERROR", "99", e.message);
    }).finally(() => {
      hideProgressWeb();
    });
  }, [location, nextPage, keyword, jusoList]);

  const handleAddressClick = async (value: any) => {
    showProgressWeb();

    // 상세 주소 Manual 입력 페이지 이동
    const moveToDeliveryInputPage = (dongResult?: string[]) => {
      navigate("/apply/step6/input",
        {
          state:{
            ...location.state,
            apply: {
              ...cardApply,
              homeZipCode: value.zipNo,
              homeEngAddress: value.roadAddr,
              homeNewAddress: value.korAddr,
              homeNewDetailAddress: "",
              homeRoadManagementNo: value.rnMgtSn,
              admCd: value.admCd,
              udrtYn: value.udrtYn,
              buldMnnm: value.buldMnnm,
              buldSlno: value.buldSlno,
              dongList: dongResult,
              siNm: value.siNm,
              sggNm: value.sggNm,
              rn: value.rn,
              emdNm: value.emdNm
            }
          }
        }
      );
    };

    await client.get("/api/app/apply/juso/searchDetail/dong", { params: { ...location.state.query,
        admCd: value.admCd,
        rnMgtSn: value.rnMgtSn,
        udrtYn: value.udrtYn,
        buldMnnm: value.buldMnnm,
        buldSlno: value.buldSlno
      }}).then(async (response) => {
      let dongResult = response.data?.response;

      if (response.data?.code === "00" && dongResult?.results) {
        moveToDeliveryInputPage(dongResult);
      } else if (response.data?.code === "02") {
        // 세션 만료 처리
        // 로그인 페이지 이동
        callAppLoginWeb();
      } else {
        moveToDeliveryInputPage();
      }
      // 응답이 에러인 경우 /step6/input 페이지로 이동
      // 응답이 에러가 아닌 경우 /step6/input/detailSelect 페이지로 이동
    }).catch((e: AxiosError) => {
      // 응답이 에러: /step6/input 페이지로 이동
      console.error(e);
      moveToDeliveryInputPage();
    }).finally(() => {
      hideProgressWeb();
    });
  };

  // 최초실행
  useEffect(() => {
    // 언어설정
    i18n.changeLanguage(location.state.query.languageCode);
    // callNative_Web 처리 (WEB > APP)
    callNativeWeb(screenId, screenName);
    // 하단바 숨기기
    footerShowEventWeb("N");

    console.log("QUERY STATE");
    console.log(location.state);
    console.log(cardApply);

    // 포커스 설정
    setTimeout(() => inputRef.current?.focus(), 500);

    // 최초한번실행 useEffect() Warning 제거
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 뒤로가기 이벤트 리스너 (backKeyEvent_App (APP > WEB))
  useEffect(() => {
    document.addEventListener("backpress", callbackBackkeyEvent);
    return () => document.removeEventListener("backpress", callbackBackkeyEvent);
  }, [callbackBackkeyEvent]);

  return (
    <div id="wrap" className={isIOS ? "isIOS" : ""}>
      <div id="container" className="pcHidden">
        <section className="contents donationBasket ">
          <div className="addressWrap">
            <div className="searchBox">
              <div className="inputBoxTy03 inputBoxTySearch">
                <input type="text" className="inpSearchForm ui-autocomplete-input" placeholder={t("APPL13.explan01")} autoComplete="off" value={keyword} onChange={(e) => setKeyword(e.target.value)} ref={inputRef} onKeyDown={(event) => { if (event.key === "Enter") callJusoFirstSearch(); }} />
                <span className="line"></span>
                <button type="button" className="icoInputDelete btnDelete" style={{ display: stringUtils.checkValueNull(keyword) ? "none" : "block" }} onClick={() => setKeyword("")}><span className="blind">삭제</span></button>
                <button type="button" className="icoSearch btnSearch" onClick={() => callJusoFirstSearch()}><span className="blind">검색</span></button>
              </div>
              <p className="message">{t("APPL13.explan02")} {t("APPL13.explan03")}</p>
            </div>
          </div>
          <div className="inner">
            <ul className="addressList">
              {jusoList &&
                <InfiniteScroll dataLength={jusoList.length} next={callJusoNextSearch} hasMore={hasMore} loader={""}>
                  {jusoList.length > 0 && jusoList.map((value) =>
                    <li className="item" onClick={() => {handleAddressClick(value)}} key={value.roadAddr}>
                      <div className="address">
                        <dl>
                          <dt>도로명</dt>
                          <dd>{value.roadAddr}</dd>
                        </dl>
                        <dl>
                          <dt>지번</dt>
                          <dd>{value.jibunAddr}</dd>
                        </dl>
                      </div>
                      <div className="add_num">
                        <p>{value.zipNo}</p>
                      </div>
                    </li>
                  )}
                </InfiniteScroll>
              }
              {jusoList && jusoList.length === 0 &&
                <li className="item">
                  <div className="address">
                    <dl>
                      <dd>{t("APPL13.nodata")}</dd>
                    </dl>
                  </div>
                </li>
              }
            </ul>
          </div>
        </section>
      </div>
    </div>
  );
};

export default DeliveryInputSearchPage;